// @/utils/extractors/aml_data_extractor.js

/**
 * Extract Surety Details from AML Results
 */
export const extractAMLSuretyDetails = (results_data) => {
  if (!results_data) return null;
  const home_affairs = results_data["Home Affairs"] || {};
  const get_value = (obj, key, default_value) =>
    obj && Reflect.get(obj, key) !== undefined && Reflect.get(obj, key) !== null
        ? Reflect.get(obj, key): default_value;

  return ({
    id_number: get_value(home_affairs, "HAIDNO", "N/A"),
    first_name: get_value(home_affairs, "HANames", "N/A"),
    last_name: get_value(home_affairs, "HASurname", "N/A"),
    date_of_birth: get_value(home_affairs, "HADateOfBirth", "N/A"),
    death_indicator: get_value(home_affairs, "HADeceasedDate", null) ? "True" : "False",
    deceased_date: get_value(home_affairs, "HADeceasedDate", null),
    id_blocked: get_value(home_affairs, "IdBlocked", "False"),
    id_match_status: get_value(home_affairs, "IDNOMatchStatus", "N/A"),
  });
};

/**
 * Extract and Summarize ALL AML Check Results
 */
export const extractAMLCheckResults = (results_data) => {
  if (!results_data) return null;

  const supplier_data = results_data["Supplier Data"] || [];

  if (supplier_data.length === 0) {
    return ({
      total_matches: 0,
      categories_summary: [],
      all_matches: [],
      highest_risk_score: 0
    });
  }

  // Build summary
  const categories_map = {};
  const all_matches = [];

  supplier_data.forEach(entry => {
    const category = entry.ReasonListed || "Unknown";

    // Count by category using Reflect
    if (!Reflect.has(categories_map, category)) {
      Reflect.set(categories_map, category, 0);
    }
    Reflect.set(categories_map, category, Reflect.get(categories_map, category) + 1);

    // Extract simplified match info
    all_matches.push({
      entity_name: entry.EntityName || "N/A",
      entity_type: entry.EntityType || "N/A",
      category: category,
      date_listed: entry.DateListed || "N/A",
      best_name_score: entry.BestNameScore || "0",
      gender: entry.Gender || "N/A",
      summary: extractBriefSummary(entry),
      occupation: extractOccupation(entry.AdditionalInfo),
      date_of_birth: extractDOB(entry.AdditionalInfo),
      comment: extractComments(entry.AdditionalInfo),
    });
  });

  // Convert categories map to array
  const categories_summary = Object.keys(categories_map).map(cat => ({
    category: cat,
    count: Reflect.get(categories_map, cat)
  }));

  // Get highest score
  const highest_score = Math.max(
    ...supplier_data.map(e => parseInt(e.BestNameScore) || 0)
  );

  return ({
    total_matches: supplier_data.length,
    categories_summary,
    all_matches,
    highest_risk_score: highest_score
  });
};

/**
 * Extract brief summary from entry
 */
const extractBriefSummary = (entry) => {
  const comments = entry.Comments || "";

  const offense_match = comments.match(/Offense:\s*([^|]+)/);
  if (offense_match) return offense_match[1].trim();

  const occupation = extractOccupation(entry.AdditionalInfo);
  if (occupation && occupation !== "N/A") return occupation;

  const notes_match = comments.match(/Profile Notes:\s*([^|]+)/);
  if (notes_match) return notes_match[1].trim().substring(0, 100) + "...";

  return entry.ReasonListed || "No additional information";
};

/**
 * Extract Occupation from AdditionalInfo
 */
const extractOccupation = (additional_info) => {
  if (!additional_info || !Array.isArray(additional_info)) return "N/A";
  const occupation_item = additional_info.find(info => info.Type === "Occupation");
  return occupation_item?.Value || "N/A";
};

/**
 * Extract DOB from AdditionalInfo
 */
const extractDOB = (additional_info) => {
  if (!additional_info || !Array.isArray(additional_info)) return "N/A";
  const dob_item = additional_info.find(info => info.Type === "DOB");
  return dob_item?.Value || "N/A";
};

/**
 * Calculate Overall Risk Score based on data
 */
export const calculateAMLRiskScore = (aml_check_results) => {
  if (!aml_check_results || aml_check_results.total_matches === 0) return "No Risk";

  const { categories_summary, highest_risk_score } = aml_check_results;
  let risk_points = 0;

  const add_risk_if_matched = (keyword, points_fn) => {
    const match_count = categories_summary.reduce(
      (sum, cat) => sum + (cat.category.includes(keyword) ? cat.count : 0),
      0
    );
    if (match_count > 0) risk_points += points_fn(highest_risk_score);
  };

  add_risk_if_matched("Enforcement", () => 3);
  add_risk_if_matched("PEP", (score) => (score >= 95 ? 2 : 1));
  add_risk_if_matched("SOE", () => 1);

  if (risk_points >= 4) return "High Risk";
  if (risk_points >= 2) return "Medium Risk";
  return "Low Risk";
};

export const extractComments = (info) => {
  if (!Array.isArray(info)) return "N/A";

  const comment_list = info
    .filter(i => i.Comment)
    .map(i => i.Comment.trim());

  return comment_list.length ? comment_list.join(" | ") : "N/A";
};